import * as React from 'react';
import { useStore } from 'react-redux';
import { FLAG_UPDATE_PAYMENT_METHOD } from 'config';
import { CONTACT_US_URL } from 'config/constants';
import { useModal } from 'lib/hooks';
import GuestDetailsModal, { SoftAmendmentProvider } from 'components/GuestDetailsModal';
import * as tracking from 'lib/gaTracking/bookingDetailsPageEvents';
import { ActionBar, ActionItem } from 'pages/BookingDetailsPage/ActionBar';
import { useDetailsBooking } from 'lib/hooks/useDetailsBooking';
import { getChangeYourBookingLink } from 'lib/links';
import { downloadInvoicePdf } from 'lib/selfServiceApi';
import { getAuthorizationHeader } from 'store/authentication/selectors';
import { useDownloadInvoiceErrorModal } from '../Alerts/Modal/DownloadInvoiceError';
import { useBookingDetailsPageContext } from '../contextProvider/BookingDetailsPageContextProvider';
import { GA } from 'types';
import sendGAEvent from 'utils/sendGAEvent';

export const ManageBookingActions: React.FC = () => {
  const {
    openModal,
    closeModal,
    modalProps: { isOpen },
  } = useModal();
  const {
    modals: {
      adyenMigrationActionableModal: { isActiveModal: isUpdatePaymentModalActive, openModal: openUpdatePaymentModal },
    },
  } = useBookingDetailsPageContext();
  const { booking } = useDetailsBooking();
  const openDownloadInvoiceErrorModal = useDownloadInvoiceErrorModal();
  const changeYourBookingUrl = getChangeYourBookingLink(booking);
  const store = useStore();

  const {
    flags: { isCancelableInApp, isChangeInProgress, isFinalising },
  } = booking;

  return (
    <>
      <ActionBar label="Manage your booking">
        {!isFinalising && (
          <ActionItem
            key="update-guest-details-link"
            dataTestId="update-guest-details-link"
            icon="contactPage"
            label="Update guest details"
            onClick={() => {
              openModal();
              tracking.updateGuestDetailsLinkSelected();
              sendGAEvent({
                type: GA.EventType.MENU_CLICK,
                itemText: GA.ItemText.UPDATE_GUEST_DETAILS,
              });
            }}
          />
        )}
        {FLAG_UPDATE_PAYMENT_METHOD && !isFinalising && (
          <ActionItem
            key="update-credit-card-details-link"
            dataTestId="update-credit-card-details-link"
            icon="creditCard"
            label="Update credit card details"
            url={booking.links.updatePaymentMethod}
            external={false}
            onClick={tracking.updateCreditCardDetailsLinkSelected}
          />
        )}
        {changeYourBookingUrl && !isFinalising && (
          <ActionItem
            key="change-your-booking-link"
            dataTestId="change-your-booking-link"
            icon="event"
            label="Change your booking"
            url={changeYourBookingUrl}
            external={false}
            onClick={(event) => {
              tracking.changeYourBookingLinkSelected();
              sendGAEvent({
                type: GA.EventType.MENU_CLICK,
                itemText: GA.ItemText.CHANGE_YOUR_BOOKING,
              });
              if (isUpdatePaymentModalActive) {
                openUpdatePaymentModal();
                event.preventDefault();
              }
            }}
          />
        )}
        {isCancelableInApp && !isFinalising && (
          <ActionItem
            key="cancel-your-booking-link"
            dataTestId="cancel-your-booking-link"
            icon="cancel"
            label="Cancel your booking"
            url={`${booking.links.manage}/cancellation-quote`}
            external={false}
            onClick={(event) => {
              tracking.cancelYourBookingLinkSelected();
              sendGAEvent({
                type: GA.EventType.MENU_CLICK,
                itemText: GA.ItemText.CANCEL_YOUR_BOOKING,
              });
              if (isUpdatePaymentModalActive) {
                openUpdatePaymentModal();
                event.preventDefault();
              }
            }}
          />
        )}
        <ActionItem
          key="contact-us-link"
          dataTestId="contact-us-link"
          icon="contactSupport"
          label="Contact us"
          url={CONTACT_US_URL}
          external={true}
          onClick={tracking.contactUsLinkSelected}
          target="_blank"
        />
        {!isChangeInProgress && (
          <ActionItem
            key="download-invoice-link"
            dataTestId="download-invoice-link"
            icon="getApp"
            label="Download invoice"
            onClick={async () => {
              tracking.downloadInvoiceLinkSelected('Manage your booking');
              try {
                await downloadInvoicePdf(booking, getAuthorizationHeader(store.getState()));
              } catch (e) {
                openDownloadInvoiceErrorModal();
              }
            }}
          />
        )}
      </ActionBar>
      {isOpen && (
        <SoftAmendmentProvider>
          <GuestDetailsModal isOpen={isOpen} closeModal={closeModal} data-testid="edit-guest-information-modal" />
        </SoftAmendmentProvider>
      )}
    </>
  );
};

export default ManageBookingActions;
